import * as React from 'react';
import { AdobePayloadData, Nullable } from '../typings';
import loadMessagesUI, { ConstructorArguments, Campaign } from '@central-products/welcome-messages-ui';
import { getCampaignFromActionContext } from '../utils/utils';
import {
  trackDisplayed,
  trackDismissed,
  trackAccepted,
  trackInteraction,
  trackError,
} from '../analytics/tracking-events.service';

export interface Props {
  onWmRequested: (cb: (action: AdobePayloadData) => void) => void;
  onCancelRequested: (cb: () => void) => void;
  onWmDisplayed: (payload: AdobePayloadData) => void;
  onCTAClicked: (url: string, target: string) => void;
  dismissUI: () => void;
  loadUI?: () => Promise<UI>;
}

interface State {
  UiComponent: Nullable<UI>;
  campaign: Nullable<Campaign>;
  adobePayload: Nullable<AdobePayloadData>;
  showUiComponent: boolean;
}

type DefaultProps = Readonly<typeof defaultProps>;

type UI = React.ComponentClass<ConstructorArguments> | React.SFC<ConstructorArguments>;

const defaultProps = {
  loadUI: loadMessagesUI,
};

const initialState: State = {
  UiComponent: null,
  campaign: null,
  adobePayload: null,
  showUiComponent: false,
};

class WelcomeMessagesProviderComponent extends React.PureComponent<Props, State> {
  public static defaultProps: DefaultProps = defaultProps;

  public readonly state: State = { ...initialState };

  public componentDidMount() {
    this.props.onWmRequested((action: AdobePayloadData) => this.handleRequestedAction(action));
    this.props.onCancelRequested(() => this.setState({ ...initialState }));
  }

  public render() {
    const { campaign, UiComponent, adobePayload, showUiComponent } = this.state;

    return (
      adobePayload &&
      campaign &&
      UiComponent &&
      showUiComponent && (
        <UiComponent
          campaign={campaign}
          deviceType={adobePayload!.deviceType}
          onDismissed={this.onDismissed}
          onAccepted={this.onAccepted}
          onInteraction={this.onInteraction}
          onDisplayed={this.onUiDisplayed}
        />
      )
    );
  }

  private async handleRequestedAction(adobePayload: AdobePayloadData): Promise<void> {
    try {
      const campaign = await getCampaignFromActionContext(adobePayload);
      const UiComponent = await this.props.loadUI!();
      if (!campaign || !UiComponent) {
        const failedComponentsLabel = `${campaign ? '' : ' campaign'}${UiComponent ? '' : ' UiComponent'}`;
        const error = new Error(`Failed to load welcome messages${failedComponentsLabel}`);
        this.handleRequestedCampaignFailure(adobePayload, error);
      } else {
        this.setState({ campaign, UiComponent, adobePayload, showUiComponent: true });
      }
    } catch (err) {
      this.handleRequestedCampaignFailure(adobePayload, err);
    }
  }

  private handleRequestedCampaignFailure(adobePayload: AdobePayloadData, err: Error) {
    const campaign = this.state.campaign || {
      campaign: adobePayload.campaignName,
      version: adobePayload.campaignVersion,
    };
    trackError(campaign, { name: err.name, message: err.message });
    this.props.dismissUI();
  }

  private onUiDisplayed = (): void => {
    this.props.onWmDisplayed(this.state.adobePayload!);
    trackDisplayed(this.state.campaign!);
  };

  private onDismissed = (label: string): void => {
    trackDismissed(this.state.campaign!, label);
    this.props.dismissUI();
  };

  private onAccepted = (ctaUrl: string, ctaTarget: string): void => {
    this.props.onCTAClicked(ctaUrl, ctaTarget);
    trackAccepted(this.state.campaign!, ctaUrl);
    this.props.dismissUI();
  };

  private onInteraction = (elId: string, buttonLabel?: string): void => {
    trackInteraction(this.state.campaign!, elId, buttonLabel);
  };
}

export default WelcomeMessagesProviderComponent;
